/**
 * Socket manager — single entry point for all WebSocket connections.
 *
 * URL resolution priority (CRA / React):
 *   1. REACT_APP_WS_URL          — explicit wss:// URL (recommended for production)
 *   2. REACT_APP_BACKEND_URL     — converted http(s):// → ws(s)://
 *   3. window.location           — derived from browser host (dev fallback only)
 *   4. ws://localhost:8001       — hard fallback
 */

import WebSocketClient from './WebSocketClient';

// ─── URL resolution ────────────────────────────────────────────────────────────

/**
 * Derive the WebSocket base URL from the available environment variables.
 * All logic is CRA-native (process.env) — no import.meta.env needed.
 */
const resolveWsBaseUrl = () => {
  // 1. Explicit WebSocket URL — highest priority, no conversion needed.
  const explicitWs = process.env.REACT_APP_WS_URL;
  if (explicitWs) {
    const url = explicitWs.replace(/\/$/, '');
    // Convert http(s):// to ws(s):// in case someone set the HTTP URL here.
    return url.replace(/^http:/, 'ws:').replace(/^https:/, 'wss:');
  }

  // 2. HTTP backend URL — convert protocol.
  const backendUrl = process.env.REACT_APP_BACKEND_URL;
  if (backendUrl && (backendUrl.startsWith('http://') || backendUrl.startsWith('https://'))) {
    return backendUrl.replace(/\/$/, '').replace(/^http:/, 'ws:').replace(/^https:/, 'wss:');
  }

  // 3. Derive from browser location (only safe in development on localhost).
  if (typeof window !== 'undefined') {
    const { protocol, hostname } = window.location;
    if (hostname === 'localhost' || hostname === '127.0.0.1') {
      // Always connect to the backend port (8001), not the CRA dev-server port.
      return `ws://${hostname}:8001`;
    }
    // Non-localhost without env vars set — warn loudly, this is almost certainly wrong.
    console.warn(
      '[WS] REACT_APP_BACKEND_URL is not set. Falling back to window.location, ' +
      'which will point WebSocket connections at the FRONTEND host — this is wrong in production. ' +
      'Set REACT_APP_BACKEND_URL (or REACT_APP_WS_URL) to the backend service URL.'
    );
    const wsProtocol = protocol === 'https:' ? 'wss:' : 'ws:';
    return `${wsProtocol}//${hostname}`;
  }

  // 4. Hard fallback.
  return 'ws://localhost:8001';
};

const WS_BASE_URL = resolveWsBaseUrl();
console.info(`[WS] Base URL resolved to: ${WS_BASE_URL}`);

// ─── Singleton instance ────────────────────────────────────────────────────────

let socketInstance = null;

// ─── Public API ───────────────────────────────────────────────────────────────

/**
 * Open a WebSocket connection.
 *
 * @param {string} path      - Server path, e.g. '/ws/projects/abc-123'
 * @param {string} token     - JWT access token (appended as ?token= query param)
 * @param {Object} callbacks - { onOpen, onMessage, onError, onClose }
 * @returns {WebSocketClient}
 */
export const initSocket = (path, token = null, callbacks = {}) => {
  // Close existing connection cleanly before opening a new one.
  if (socketInstance) {
    socketInstance.close();
    socketInstance = null;
  }

  const normalizedPath = path.startsWith('/') ? path : `/${path}`;
  let fullUrl = `${WS_BASE_URL}${normalizedPath}`;

  if (token) {
    const sep = fullUrl.includes('?') ? '&' : '?';
    fullUrl = `${fullUrl}${sep}token=${encodeURIComponent(token)}`;
  }

  // Log the URL (mask most of the token for security in browser console).
  const maskedUrl = token
    ? fullUrl.replace(/token=[^&]+/, `token=${token.slice(0, 10)}…`)
    : fullUrl;
  console.info(`[WS] Opening connection: ${maskedUrl}`);

  socketInstance = new WebSocketClient(fullUrl, {
    maxReconnectAttempts: 10,
    reconnectDelay: 1000,
    maxReconnectDelay: 30000,
    shouldReconnect: true,
    onOpen:    (event) => { if (callbacks.onOpen)    callbacks.onOpen(event); },
    onMessage: (event) => { if (callbacks.onMessage) callbacks.onMessage(event); },
    onError:   (error) => {
      console.error('[WS] Socket error:', error);
      if (callbacks.onError) callbacks.onError(error);
    },
    onClose:   (event) => { if (callbacks.onClose)   callbacks.onClose(event); },
  });

  socketInstance.connect();
  return socketInstance;
};

export const closeSocket = () => {
  if (socketInstance) {
    socketInstance.close();
    socketInstance = null;
  }
};

export const getSocket  = () => socketInstance;

export const sendMessage = (data) => {
  if (socketInstance) {
    socketInstance.send(data);
  } else {
    console.warn('[WS] sendMessage called but no socket is open.');
  }
};
